/* eslint-disable no-shadow */
import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import {
  Box, Typography, Card, IconButton, Container, Divider, MenuItem, Grid, CardHeader, CardContent, Avatar, Button
} from "@mui/material";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {getImage, isMarketPlace} from "helpers/utils";

import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import AddBoxSharpIcon from "@mui/icons-material/AddBoxSharp";
import IndeterminateCheckBoxSharpIcon from "@mui/icons-material/IndeterminateCheckBoxSharp";
import ButtonComponent from "components/Button";
import MockProductList from "components/elements/main/MockProductList";
import { H5 } from "components/CustomTypography";

import * as Actions from "actions";
import * as ActionTypes from "constants/ActionTypes";

import { getCartTotal } from "services";
import { CURRENCY } from "config/constants";
import Sticky from "components/sticky/Sticky";
import ShopHeaderMini from "components/elements/main/ShopHeaderMini";
import SelectFieldComponent from "components/SelectField";
import {Notification} from "components/Atoms";
import EmptyShoppingCart from "components/elements/main/EmptyShoppingCart";

const ShopCart = (props) => {
  const {
    serviceError, reqStatus, isLoading,
    cartItems, symbol, total, getCartProducts, bootupInfo, removeProductFromCart,
    incrementProductInCart, decrementProductInCart, updateProductSizeInCart, wholesale, match,
  } = props;

  const shopId = (isMarketPlace() ? match.params.shopId : bootupInfo.appState.subdomain_shopId);

  useEffect(() => {
    getCartProducts(bootupInfo.appState.subdomain_shopId, bootupInfo.appState.isWebsite);
  }, []);

  const sendNotification = (type, { title, description = "" }) => {
    Notification[type]({
      message: title,
      description,
    });
  };

  useEffect(() => {
    if (serviceError && reqStatus) {
      const feedback = {
        title: serviceError,
        description: serviceError,
      };
      sendNotification("error", feedback);
    }
    switch (reqStatus) {
      case ActionTypes.USER_UPDATE_PRODUCT_SIZE_IN_CART_SUCCESS:
        getCartProducts(bootupInfo.appState.subdomain_shopId, bootupInfo.appState.isWebsite);
        break;
      default:
        break;
    }
  }, [serviceError, reqStatus]);

  let checkOutLink;

  const getPrice = (symbol, item) => {
    const price = item.seller_price
    - (item.product && item.product.discount
      ? (item.seller_price * (parseInt(item.product.discount, 10) / 100)) : 0);
    return (
      <Typography variant="caption" gutterBottom>
        {symbol}
        {price}
        {item.product.discount && (
        <del>
          <span className="money">
            {symbol}
            {item.seller_price}
          </span>
        </del>
        )}
      </Typography>
    );
  };

  const handleSizeClick = (event, item) => {
    updateProductSizeInCart(item, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite, event.target.value);
  };

  const validateSizeInCart = () => {

    let valid = true;

    cartItems.map(function (item) {
      if (item.size && item.size.length > 0) {
        if (!(item.size.includes(item.product_size))) {
          valid = false;
        }
      }
    });

    return valid;
  };

  const getSize = (item) => {

    const availablesize = item.size;

    if (!(availablesize.length > 0)) {
      return (<></>);
    }

    const inputSizeOptions = availablesize.map((item) => {
      return (
          <MenuItem value={item}>{item}</MenuItem>
      );
    });

    return (
        <Typography variant="caption" gutterBottom>
          {item.size && (
              <SelectFieldComponent
              options={inputSizeOptions}
              label="Size"
              value={item.product_size}
              onChange={(event) => handleSizeClick(event, item)}
          />
          )}
        </Typography>
    );
  };

  const getquantity = (cartItem) => (
    <Box display="flex">
      <IconButton color="primary" size="small" onClick={cartItem.product_quantity > 1 && !isLoading ? () => decrementProductInCart(cartItem, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite) : ""}>
        <IndeterminateCheckBoxSharpIcon />
      </IconButton>
      <Typography className="p-1">{cartItem.product_quantity}</Typography>
      <IconButton color="primary" size="small" onClick={!isLoading ? () => incrementProductInCart(cartItem, props.bootupInfo.appState.subdomain_shopId, props.bootupInfo.appState.isWebsite) : ""}>
        <AddBoxSharpIcon />
      </IconButton>
    </Box>
  );

  const history = useHistory();

  const handleCheckoutPage = (page) => (e) => {
    if (validateSizeInCart()) {
      history.push(page);
    } else {
      sendNotification("error", {title: "Size", description: "Select a proper size"});
    }
  };

  if (bootupInfo.appState.isWebsite) {
    checkOutLink = "/cart/checkout";
  } else {
    checkOutLink = `/shop/${props.bootupInfo.appState.subdomain_shopId}/cart/checkout`;
  }

  const renderOrderItems = () => (
    <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader 
            avatar={<ShoppingCartIcon sx={{ color: 'primary.main' }} />} 
            title={`Shopping Cart (${cartItems.length} items)`} 
            titleTypographyProps={{ fontWeight: 600 }} 
            sx={{ bgcolor: 'grey.200' }} 
        />
        <CardContent>
            {cartItems.map((item, index) => (
                <React.Fragment key={index}>
                    <Box display="flex" alignItems="center" my={1.5}>
                        <Avatar variant="rounded" src={getImage(item.product.images)} sx={{ width: 80, height: 80, mr: 2 }} />
                        <Box flexGrow={1}>
                            <Typography fontWeight={600} variant="subtitle1">{item.product.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {getPrice(symbol, item)}
                            </Typography>
                            <Box mt={1}>
                                {item.size && getSize(item)}
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="flex-end">
                             <Typography fontWeight={600} variant="subtitle1">
                                {symbol}{(item.product_quantity * (item.seller_price - (item.product && item.product.discount ? (item.seller_price * (parseInt(item.product.discount, 10) / 100)) : 0))).toFixed(2)}
                             </Typography>
                             <Box mt={1}>
                                {getquantity(item)}
                             </Box>
                             <IconButton size="small" color="error" onClick={() => removeProductFromCart(item, bootupInfo.appState.subdomain_shopId, bootupInfo.appState.isWebsite)}>
                                <ClearSharpIcon fontSize="small" />
                             </IconButton>
                        </Box>
                    </Box>
                    {index < cartItems.length - 1 && <Divider />}
                </React.Fragment>
            ))}
        </CardContent>
    </Card>
  );

  const renderCartSummary = () => (
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>Order Summary</Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography fontWeight={600}>{symbol}{total}</Typography>
          </Box>
           <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Shipping and taxes calculated at checkout.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">{symbol}{total}</Typography>
          </Box>
          <ButtonComponent
                variant="contained"
                type="primary"
                name="Proceed to Checkout"
                fullWidth
                size="large"
                onClick={handleCheckoutPage(checkOutLink)}
            />
      </Box>
  );

  const renderEmptyCart = () => (
    <Container>
      <Box>
        <Typography className="m-2" align="center">
        <EmptyShoppingCart />
          <H5>
            Cart is Empty
          </H5>
        </Typography>
      </Box>
    </Container>
  );

    return (
    <div>
      <Container sx={{ py: 3 }}>
          {isLoading ? <MockProductList /> : (
              cartItems.length > 0 ? (
                  <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                          {renderOrderItems()}
                      </Grid>
                      <Grid item xs={12} md={4}>
                          {renderCartSummary()}
                      </Grid>
                  </Grid>
              ) : renderEmptyCart()
          )}
      </Container>
    </div>
  );
};

ShopCart.defaultProps = {
    symbol: CURRENCY,
};

ShopCart.propTypes = {
  bootupInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  removeProductFromCart: PropTypes.func.isRequired,
  incrementProductInCart: PropTypes.func.isRequired,
  decrementProductInCart: PropTypes.func.isRequired,
  getCartProducts: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  symbol: PropTypes.string.isRequired,
};

const shopCart = connect(
  ({ cartList, bootupInfo }) => ({
    bootupInfo,
      symbol: bootupInfo.symbol,
    reqStatus: cartList.reqStatus,
    isLoading: cartList.isLoading,
    serviceError: cartList.serviceError,
    cartItems: cartList.cart,
    total: getCartTotal(cartList.cart),
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch),
)(ShopCart);

export { shopCart as default };
